import { toJson } from "../erd-editor-schema/dist/parser.js";
import { toDocumentJson } from "../erd-editor-schema/dist/storageForm.js";
import { arrayHas } from "../utils/arrayHas.js";
import { changeViewportAction } from "./modules/editor/atom.actions.js";
import { safeCallback } from "../utils/safeCallback.js";
import { initialLoadJsonAction$ } from "./modules/editor/generator.actions.js";
import { ReplicaActionTypes, ReplicaChangeActionTypes, batchVersion, mergeBatchVersions } from "./actions.js";
import { slotOf } from "./clock.js";
import { createEngineContext } from "./context.js";
import { actionsFilter } from "./rx-operators/actionsFilter.js";
import { createStore } from "./store.js";
import { createHooks } from "./store-hooks.js";
import { toLoadValue } from "../utils/loadValue.js";
import { omit } from "es-toolkit";
import { Observable, Subject, debounceTime, map } from "rxjs";
//#region src/engine/replication-store.ts
var isReplicaChange = arrayHas(ReplicaChangeActionTypes);
var InternalActionType = { change: "change" };
function createReplicationStore(context) {
	const subscriptionSet = /* @__PURE__ */ new Set();
	const engineContext = {
		...createEngineContext({
			...context,
			routes: false
		}),
		remote: true
	};
	const { clock } = engineContext;
	const store = createStore(engineContext, false);
	clock.setSlot(slotOf(store.state.editor.id));
	store.dispatchSync(changeViewportAction({
		width: 0,
		height: 0
	}));
	const hooks = createHooks(store);
	const dispatch$ = new Subject();
	const change$ = new Observable((subscriber) => store.subscribe((actions) => subscriber.next(actions))).pipe(actionsFilter(ReplicaChangeActionTypes), debounceTime(200));
	const observers = /* @__PURE__ */ new Set();
	let baseline = null;
	const on = (listeners) => {
		observers.has(listeners) || observers.add(listeners);
		return () => {
			observers.delete(listeners);
		};
	};
	const emit = (type, payload) => {
		observers.forEach((listeners) => {
			const listener = Reflect.get(listeners, type);
			safeCallback(listener, payload);
		});
	};
	const setInitialValue = (value) => {
		baseline = null;
		store.dispatchSync(initialLoadJsonAction$(toLoadValue(value)));
	};
	const dispatchSync = (actions) => {
		dispatch$.next([actions].flat());
	};
	const dispatch = (actions) => {
		queueMicrotask(() => dispatchSync(actions));
	};
	const destroy = () => {
		Array.from(subscriptionSet).forEach((sub) => sub.unsubscribe());
		subscriptionSet.clear();
		observers.clear();
		store.destroy();
		hooks.destroy();
	};
	const handleChange = () => {
		const value = toDocumentJson(store.state);
		const changed = baseline !== null && value !== baseline;
		if (baseline !== null) baseline = value;
		emit(InternalActionType.change, {
			value,
			changed
		});
	};
	subscriptionSet.add(change$.subscribe(handleChange)).add(dispatch$.pipe(actionsFilter(ReplicaActionTypes), map((actions) => actions.map((action) => omit(action, ["tags"])))).subscribe((actions) => {
		if (actions.some(({ type }) => isReplicaChange(type))) baseline ??= toDocumentJson(store.state);
		const version = batchVersion(clock, actions);
		actions.forEach((action) => action.version ??= version);
		store.dispatchSync(actions);
		mergeBatchVersions(clock, actions);
	}));
	return Object.freeze({
		get value() {
			return toDocumentJson(store.state);
		},
		get runtimeValue() {
			return toJson(store.state);
		},
		on,
		setInitialValue,
		dispatch,
		dispatchSync,
		destroy
	});
}
//#endregion
export { createReplicationStore };
